import { withEventContext } from "@codat/analytics";
import { Button, Typography } from "@codat/orchard-ui";
import { ApiImage } from "api/shared";
import classNames from "classnames";
import { useConfig } from "contexts/ConfigContext";
import { SourceTypeStatus, useSourceTypes } from "contexts/SourceTypesContext";
import useNextSourceTypeRedirectHandler from "hooks/useNextSourceTypeRedirectHandler";
import { useSourceTypeResolver } from "hooks/useSourceTypeResolver";
import { useParams } from "react-router-dom";
import { usePageViewedEvent } from "utils";

import SecondaryCta from "components/SecondaryCta";
import { CompanySourceTypeParams } from "config/urls";

import { ContactSupport } from "../ContactSupport/ContactSupport";
import styles from "./PlaidUpgrade.module.css";

export interface PlaidUpgradeProps {
  integrationLogo?: ApiImage;
  isComplete?: boolean;
}

const PlaidUpgradeContent: React.FC<PlaidUpgradeProps> = ({
  integrationLogo,
  isComplete,
}) => {
  const { companyId, sourceType }: CompanySourceTypeParams = useParams();
  const { sourceTypes, status: sourceTypesStatus } = useSourceTypes();
  const {
    state: { data: linkConfig },
  } = useConfig();

  const { currentSourceType, nextPrioritySourceType } = useSourceTypeResolver(
    { sourceType: sourceType },
    sourceTypes
  );

  const handleSkip = useNextSourceTypeRedirectHandler(
    companyId,
    nextPrioritySourceType,
    currentSourceType && !nextPrioritySourceType
  );

  usePageViewedEvent({
    ready: !!linkConfig,
    clientConfig: linkConfig,
  });

  return (
    <div
      data-testid="plaid-upgrade"
      className={classNames(styles.content, styles.centered, {
        [styles.show]:
          isComplete && sourceTypesStatus === SourceTypeStatus.READY,
      })}
    >
      <header className={styles.header}>
        {integrationLogo?.src && (
          <img
            className={styles.image}
            src={integrationLogo.src}
            alt={integrationLogo.alt}
          />
        )}
        <Typography
          variant="h2"
          as="h2"
          data-testid="plaid-upgrade-title"
          className={styles.title}
        >
          This software is disabled
        </Typography>
      </header>
      <div className={styles.contactSupport}>
        <ContactSupport
          clientName={linkConfig.clientName}
          link={linkConfig.help?.link}
        />
      </div>
      <div className={styles.footer}>
        <SecondaryCta
          label="Skip this step"
          analyticsName="Skip"
          data-testid="skip-btn"
          onClick={handleSkip}
        />
        <Button
          data-testid="continue-button"
          label="Continue"
          disabled
          className={styles.continueButton}
        />
      </div>
    </div>
  );
};

export const PlaidUpgrade = withEventContext<
  PlaidUpgradeProps & JSX.IntrinsicAttributes
>(PlaidUpgradeContent, "Plaid upgrade page");
